/**
 * Small synthesized sound effects (Web Audio, no files). Every call is a no-op
 * when muted or when audio is unavailable. The game has ONE AudioContext, kept
 * alive by the audio keeper (lib/audioKeeper.ts, ported from Single Didge): the
 * first tap creates and unlocks it, later taps and returns to the page bring it
 * back after iOS interruptions. Tibbs's voice plays on the same context.
 */
import { createAudioKeeper, declareAmbientSession } from './audioKeeper';
import { KEYS, load, save } from './storage';

let master: GainNode | null = null;
let masterCtx: AudioContext | null = null;
let muted = load<boolean>(KEYS.muted, false);
const muteListeners = new Set<(m: boolean) => void>();
/** Effects play at this level; Tibbs ducks it by 6 dB while he talks. */
const SFX_LEVEL = 0.5;
let ducked = false;

const AC: typeof AudioContext | undefined =
  typeof window !== 'undefined' ? (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) : undefined;
/** Null where there is no Web Audio at all (old browsers, tests): every sound is then a no-op. */
const keeper = AC ? createAudioKeeper<AudioContext>({ create: () => new AC() }) : null;

function sfxOut(c: AudioContext): GainNode {
  if (!master || masterCtx !== c) {
    master = c.createGain();
    master.gain.value = ducked ? SFX_LEVEL / 2 : SFX_LEVEL;
    master.connect(c.destination);
    masterCtx = c;
  }
  return master;
}

/** The game's audio context once a tap has unlocked it, else null (browsers forbid audio before a gesture). */
export function audioContext(): AudioContext | null {
  if (!keeper || !keeper.current()) return null;
  try {
    return keeper.get();
  } catch {
    return null;
  }
}

function audio(): AudioContext | null {
  if (muted) return null;
  return audioContext();
}

/** Call from a user gesture to unlock audio on iOS/Safari (the keeper also listens for taps on its own). */
export function unlockAudio(): void {
  if (!keeper) return;
  try {
    const c = keeper.get();
    if (c.state !== 'running') void keeper.resume();
  } catch {
    /* no Web Audio */
  }
}

/** Tibbs is talking: effects drop 6 dB, and come back after. */
export function duckEffects(on: boolean): void {
  ducked = on;
  const c = masterCtx;
  if (!master || !c) return;
  const g = master.gain;
  const t = c.currentTime;
  g.cancelScheduledValues(t);
  g.setValueAtTime(g.value, t);
  g.linearRampToValueAtTime(on ? SFX_LEVEL / 2 : SFX_LEVEL, t + 0.15);
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(m: boolean): void {
  muted = m;
  save(KEYS.muted, m);
  if (!m) unlockAudio();
  muteListeners.forEach((fn) => fn(m));
}

export function onMuteChange(fn: (m: boolean) => void): () => void {
  muteListeners.add(fn);
  return () => muteListeners.delete(fn);
}

function tone(freq: number, start: number, dur: number, type: OscillatorType = 'sine', vol = 0.25): void {
  const c = audio();
  if (!c) return;
  const out = sfxOut(c);
  const t = c.currentTime + start;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(out);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

function noise(start: number, dur: number, freq: number, vol = 0.3, q = 1.2): void {
  const c = audio();
  if (!c) return;
  const out = sfxOut(c);
  const t = c.currentTime + start;
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = freq;
  filter.Q.value = q;
  const g = c.createGain();
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filter).connect(g).connect(out);
  src.start(t);
}

export const sfx = {
  /** Card sliding off the pile. */
  flick: () => noise(0, 0.12, 2400, 0.35),
  /** Card-stock snap on a flip. */
  flip: () => {
    noise(0, 0.05, 4200, 0.4, 2);
    tone(900, 0.01, 0.05, 'triangle', 0.08);
  },
  /** Card landing in a slot. */
  tap: () => tone(520, 0, 0.06, 'triangle', 0.15),
  /** Deal: n soft swishes. */
  deal: (n = 8) => {
    for (let i = 0; i < Math.min(n, 16); i++) noise(i * 0.05, 0.08, 2000 + i * 40, 0.18);
  },
  /** Soft chime for a matched column. */
  pair: () => {
    tone(1046.5, 0, 0.18, 'sine', 0.22);
    tone(1318.5, 0.08, 0.22, 'sine', 0.2);
    tone(1568, 0.16, 0.35, 'sine', 0.18);
  },
  /** Hole-in-One (−5) moment: a sparkly glissando. */
  sparkle: () => {
    [1568, 1760, 2093, 2349, 2637].forEach((f, i) => tone(f, i * 0.05, 0.25, 'sine', 0.12));
  },
  /** Your turn: a soft two-note chime (it replaces "Your turn" text, so it rings a little longer). */
  turn: () => {
    tone(784, 0, 0.2, 'sine', 0.2);
    tone(1175, 0.14, 0.32, 'sine', 0.2);
    tone(2350, 0.14, 0.18, 'sine', 0.03);
  },
  /** Someone went out: C-E-G-C fanfare. */
  out: () => {
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, i * 0.11, 0.3, 'triangle', 0.22));
  },
  /** Ball in the cup before scoring. */
  plonk: () => {
    tone(220, 0, 0.18, 'sine', 0.3);
    noise(0.02, 0.1, 600, 0.2, 3);
  },
  /** One counter tick per column on the score sheet. */
  tick: () => tone(1400, 0, 0.03, 'square', 0.04),
  /** Soft error blip. */
  error: () => tone(160, 0, 0.12, 'square', 0.08),
  /** Reaction sticker pop. */
  pop: () => tone(880, 0, 0.07, 'sine', 0.15),
  /** Game win jingle. */
  win: () => {
    const notes = [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5, 1318.5];
    notes.forEach((f, i) => tone(f, i * 0.13, 0.32, 'triangle', 0.2));
  },
};

if (typeof window !== 'undefined') {
  // iOS: effects mix with the player's own music and obey the silent switch; Tibbs holds "playback" only while he talks.
  declareAmbientSession();
  keeper?.install();
}
